/**
 * FeedbackViewer Component
 * Displays user feedback from "Help Us Improve" surveys for segment admins
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import AdminApiService from '../services/AdminApiService';
import SurveyQuestionManager from './SurveyQuestionManager';
import './FeedbackViewer.css';

const FeedbackViewer = () => {
  const { user } = useAuth();
  const [subTab, setSubTab]             = useState('responses'); // 'responses' | 'questions'
  const [feedback, setFeedback]         = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [dateFilter, setDateFilter]     = useState({ startDate: '', endDate: '' });
  const [expandedEvents, setExpandedEvents] = useState(new Set(['__first__']));

  useEffect(() => {
    loadFeedback();
  }, []);

  const loadFeedback = async (startDate = null, endDate = null) => {
    setLoading(true);
    setError(null);

    try {
      const response = await AdminApiService.getFeedback(startDate, endDate);
      setFeedback(response.data);
      // Auto-expand the first (newest) event group
      if (response.data?.eventGroups?.length > 0) {
        const firstKey = eventGroupKey(response.data.eventGroups[0]);
        setExpandedEvents(new Set([firstKey]));
      }
    } catch (err) {
      console.error('Failed to load feedback:', err);
      setError(err.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterApply = () => {
    loadFeedback(dateFilter.startDate || null, dateFilter.endDate || null);
  };

  const handleFilterReset = () => {
    setDateFilter({ startDate: '', endDate: '' });
    loadFeedback();
  };

  const eventGroupKey = (eg) => eg.eventId ? String(eg.eventId) : 'unassigned';

  const toggleEvent = (key) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAnswerDisplay = (answer) => {
    if (Array.isArray(answer)) {
      return answer.join(', ');
    }
    return answer || 'No response';
  };

  if (loading) {
    return (
      <div className="feedback-viewer">
        <div className="loading-message">Loading feedback...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feedback-viewer">
        <div className="error-message">❌ {error}</div>
      </div>
    );
  }

  const hasResponses = feedback && feedback.responses && feedback.responses.length > 0;
  const eventGroups  = feedback?.eventGroups || [];
  const isFiltered   = dateFilter.startDate || dateFilter.endDate;

  return (
    <div className="feedback-viewer">
      {/* Sub-tab nav */}
      <div className="fv-subtabs">
        <button
          className={`fv-subtab ${subTab === 'responses' ? 'fv-subtab--active' : ''}`}
          onClick={() => setSubTab('responses')}
        >
          📊 Responses
        </button>
        <button
          className={`fv-subtab ${subTab === 'questions' ? 'fv-subtab--active' : ''}`}
          onClick={() => setSubTab('questions')}
        >
          ❓ Manage Questions
        </button>
      </div>

      {/* ── Manage Questions tab ── */}
      {subTab === 'questions' && (
        <SurveyQuestionManager />
      )}

      {/* ── Responses tab ── */}
      {subTab === 'responses' && (
        <>
      {/* Header + Stats (always shown when we have feedback data) */}
      {feedback && (
      <div className="feedback-header">
        <div className="header-left">
          <h2>📝 User Feedback</h2>
          <p className="segment-info">Segment: <strong>{user.segment}</strong></p>
        </div>
        <div className="feedback-stats">
          <div className="stat-item">
            <span className="stat-label">Total Responses:</span>
            <span className="stat-value">{feedback.totalResponses}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Exported:</span>
            <span className="stat-value">{new Date(feedback.exportDate).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
      )}

      {/* Date Filter — always visible so the user can reset */}
      {feedback && (
      <div className="filter-panel">
        <div className="filter-group">
          <label>Start Date:</label>
          <input
            type="date"
            value={dateFilter.startDate}
            onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
          />
        </div>
        <div className="filter-group">
          <label>End Date:</label>
          <input
            type="date"
            value={dateFilter.endDate}
            onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
          />
        </div>
        <button onClick={handleFilterApply} className="btn-filter">
          Apply Filter
        </button>
        <button onClick={handleFilterReset} className="btn-reset">
          Reset
        </button>
      </div>
      )}

      {!hasResponses ? (
        <div className="empty-state">
          {isFiltered ? (
            <>
              <p>No responses match the selected date range.</p>
              <p className="muted">
                <button onClick={handleFilterReset} className="btn-reset" style={{ marginTop: '0.5rem' }}>
                  Reset Filter
                </button>
              </p>
            </>
          ) : (
            <>
              <p>No feedback responses yet for your segment.</p>
              <p className="muted">Responses will appear here when users submit the "Help Us Improve" survey.</p>
            </>
          )}
        </div>
      ) : (
      <>
      {/* Event-grouped accordion */}
      <div className="event-groups">
        {eventGroups.map((eg) => {
          const key = eventGroupKey(eg);
          const isOpen = expandedEvents.has(key);
          return (
            <div key={key} className={`event-group ${isOpen ? 'event-group--open' : ''}`}>
              <button
                className="event-group-header"
                onClick={() => toggleEvent(key)}
                aria-expanded={isOpen}
              >
                <div className="event-group-title">
                  <span className="event-group-icon">📅</span>
                  <span className="event-group-name">
                    {eg.eventName !== 'No Event' ? eg.eventName : 'Unassigned Responses'}
                  </span>
                  {eg.eventDate && (
                    <span className="event-group-date">{formatEventDate(eg.eventDate)}</span>
                  )}
                </div>
                <div className="event-group-meta">
                  <span className="event-group-count">{eg.responses.length} response{eg.responses.length !== 1 ? 's' : ''}</span>
                  <span className="event-group-chevron">{isOpen ? '▲' : '▼'}</span>
                </div>
              </button>

              {isOpen && (
                <div className="event-group-body">
                  <div className="responses-list">
                    {eg.responses.map((response, index) => (
                      <div key={index} className="response-card">
                        <div className="response-header">
                          <span className="response-number">Response #{index + 1}</span>
                          <span className="response-date">{formatDate(response.submittedAt)}</span>
                        </div>
                        <div className="response-answers">
                          {feedback.questions.map(question => {
                            const answer = response.answers.find(a => a.questionId === question.id);
                            return (
                              <div key={question.id} className="answer-block">
                                <div className="answer-question" title={question.text}>{question.text}</div>
                                <div className="answer-response">
                                  {answer ? getAnswerDisplay(answer.answer) : <span style={{color:'#aaa',fontStyle:'italic'}}>—</span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>
    )}
        </>
      )}
    </div>
  );
};

export default FeedbackViewer;
